import React, { useEffect, useState } from 'react';
import { Button, Popconfirm, Flex, Alert, Tooltip } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { UserAddOutlined, EditOutlined, DeleteOutlined, DownloadOutlined, EyeOutlined, CopyOutlined, CheckCircleOutlined, ApartmentOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom"
import * as actions from "store/actions/AuthActions"
import dayjs from 'dayjs';
import { Modal } from "antd";
import axios from 'axios';
import fileDownload from "js-file-download";
import { notify } from "components";
import PdfPreviewModal from "components/PdfPreviewModal/PdfPreviewModal";
import FilterBar from 'components/Common/FilterBar';
import useUrlFilters from 'hooks/useUrlFilters';

// Search and paging live in the URL so a filtered list can be shared as a link.
const URL_FILTERS = {
  search: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 50 },
};

const DocumentsTable = ({ isProfileLocked = false }) => {

  const navigate = useNavigate();
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);

  const searchTerm = filters.search || "";
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  // Keep the box in step when the URL changes underneath it (back/forward).
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

  // Frontend pagination state
  const currentPage = filters.page;
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const [totalRows, setTotalRows] = useState(0);

  const [allDocuments, setAllDocuments] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);

  const [previewPdfVisible, setPreviewPdfVisible] = useState(false);
  const [pdfPreviewData, setPdfPreviewData] = useState(null);

  // Bulk selection state
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const fetchAllDocuments = async () => {
    setLoading(true);
    try {
      const response = await actions.loadAllDocuments(searchTerm);
      if (response.status === 200) {
        const data = response.data;
        setAllDocuments(data.results || []);
        setTotalRows(data.count || data.results?.length || 0);
      }
    } catch (e) {
      notify("error", "Error fetching documents");
    }
    setLoading(false);
  };

  // Frontend pagination logic
  const paginateData = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = allDocuments.slice(startIndex, endIndex);
    setDataSource(paginatedData);
  };

  const onSearch = (value) => {
    if (searchTerm === value) {
      return;
    } else {
      setFilters({ search: value, page: 1 });
    }
  };


  useEffect(() => {
    fetchAllDocuments();
  }, [searchTerm]);

  // Apply frontend pagination when data or page changes
  useEffect(() => {
    paginateData();
  }, [allDocuments, currentPage, pageSize]);

  const handleDelete = async (id) => {
    if (isProfileLocked) {
      notify("warning", "Cannot delete documents - Profile is locked");
      return;
    }
    try {
      const response = await actions.deleteDocument(id);
      if (response?.success || response === true) {
        notify("success", "Document deleted successfully");
        fetchAllDocuments();
      }
    } catch (error) {
      notify("error", "Failed to delete document");
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (isProfileLocked) {
      notify("warning", "Cannot delete documents - Profile is locked");
      return;
    }

    if (selectedRowKeys.length === 0) {
      notify("warning", "Please select documents to delete");
      return;
    }

    Modal.confirm({
      title: `Delete ${selectedRowKeys.length} document(s)?`,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          let successCount = 0;
          for (const id of selectedRowKeys) {
            const response = await actions.deleteDocument(id);
            if (response?.success || response === true) {
              successCount++;
            }
          }
          notify("success", `${successCount} document(s) deleted successfully`);
          setSelectedRowKeys([]);
          setSelectedRows([]);
          fetchAllDocuments();
        } catch (error) {
          notify("error", "Failed to delete some documents");
        }
      },
    });
  };

  // Bulk download handler
  const handleBulkDownload = async () => {
    if (isProfileLocked) {
      notify("warning", "Cannot download documents - Profile is locked");
      return;
    }

    if (selectedRows.length === 0) {
      notify("warning", "Please select documents to download");
      return;
    }

    for (const record of selectedRows) {
      await viewDownloadDocument(record, true, false);
    }
    notify("success", `Downloaded ${selectedRows.length} document(s)`);
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
  };

  const viewDownloadDocument = async (record, download, preview) => {
    if (isProfileLocked && download) {
      notify("warning", "Cannot download documents - Profile is locked");
      return;
    }

    // Use signed document if available, otherwise original
    const fileUrl = record.is_signed && record.signed_document_file
      ? record.signed_document_file
      : record.document_file;

    // For preview, pass URL directly to react-pdf (it handles fetching internally)
    if (preview) {
      if (record.extension === ".pdf") {
        setPdfPreviewData(fileUrl);  // Pass URL directly (signed or original)
        setPreviewPdfVisible(true);
      } else {
        notify("error", "Only PDFs can be previewed");
      }
      return;
    }

    // For download, fetch as blob
    if (download) {
      try {
        const response = await axios.get(fileUrl, {
          responseType: "blob",
        });
        if (response.status === 200) {
          // Use appropriate filename for signed documents
          const filename = record.is_signed && record.signed_document_file
            ? `signed_${record.name}`
            : record.name;
          fileDownload(response.data, filename);
        }
      } catch (err) {
        notify("error", "Failed to download document");
      }
    }
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys, rows) => {
      setSelectedRowKeys(keys);
      setSelectedRows(rows);
    },
  }


  const defaultColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a?.id.localeCompare(b?.id),
      width:220,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a?.name.localeCompare(b?.name),
      width:220,
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width:220,
      sorter: (a, b) => dayjs(a?.created_at) - dayjs(b?.created_at),
      render: (text, record) => {
        return <div>{dayjs(text).format('MMMM Do YYYY, h:mm:ss a')}</div>
      }
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      width:220,
      sorter: (a, b) => a?.org_size - b?.org_size,
    },
    {
      title: 'Linked',
      dataIndex: 'is_linked',
      key: 'is_linked',
      width:220,
      sorter: (a, b) => a?.is_linked - b?.is_linked,
      render: (_, record) => {
        const size = 8;
        const radius = "30%";
        return (
          <div className='d-flex justify-content-center align-items-center'>
            {record.is_linked ? <>
              <div style={{ width: size, height: size, backgroundColor: "green", borderRadius: radius }}>

              </div>
            </> : <>
              <div style={{ width: size, height: size, backgroundColor: "red", borderRadius: radius }}>

              </div>
            </>}
          </div>
        )
      }
    },
    {
      title: 'Signed',
      dataIndex: 'is_signed',
      key: 'is_signed',
      width: 100,
      sorter: (a, b) => (a?.is_signed ? 1 : 0) - (b?.is_signed ? 1 : 0),
      render: (_, record) => (
        <div className='d-flex justify-content-center align-items-center'>
          {record.is_signed ? (
            <CheckCircleOutlined style={{ color: 'green', fontSize: 16 }} />
          ) : (
            <span style={{ color: '#999' }}>-</span>
          )}
        </div>
      )
    },
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      width:220,
      render: (_, record) => {
        // console.log(record)
        return dataSource?.length >= 1 ? (
          <Flex gap="middle">
            <Tooltip title="View Document">
              <div style={{ cursor: "pointer" }} onClick={() => viewDownloadDocument(record, false, true)}>
                <EyeOutlined />
              </div>
            </Tooltip>
            <Tooltip title={isProfileLocked ? "Profile locked - Cannot download" : "Download Document"}>
              <div
                style={{
                  cursor: isProfileLocked ? "not-allowed" : "pointer",
                  opacity: isProfileLocked ? 0.5 : 1
                }}
                onClick={() => viewDownloadDocument(record, true, false)}
              >
                <DownloadOutlined />
              </div>
            </Tooltip>
            <Tooltip title={isProfileLocked ? "Profile locked - Cannot delete" : "Delete Document"}>
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => handleDelete(record.id)}
                disabled={isProfileLocked}
              >
                <a style={{
                  opacity: isProfileLocked ? 0.5 : 1,
                  cursor: isProfileLocked ? "not-allowed" : "pointer"
                }}>
                  <DeleteOutlined />
                </a>
              </Popconfirm>
            </Tooltip>
            {record.extension === ".pdf" && (
              <Tooltip title={isProfileLocked ? "Profile locked" : "Configure Signing Workflow"}>
                <div
                  style={{
                    cursor: isProfileLocked ? "not-allowed" : "pointer",
                    opacity: isProfileLocked ? 0.5 : 1
                  }}
                  onClick={() => {
                    if (!isProfileLocked) {
                      navigate(`/documents/workflow/${record.id}`);
                    }
                  }}
                >
                  <ApartmentOutlined />
                </div>
              </Tooltip>
            )}
            {record.extension === ".pdf" && (
              <Tooltip title={
                isProfileLocked
                  ? "Profile locked - Cannot sign"
                  : record.is_signed
                    ? "Add More Signatures"
                    : "Sign Document"
              }>
                <div
                  style={{
                    cursor: isProfileLocked ? "not-allowed" : "pointer",
                    opacity: isProfileLocked ? 0.5 : 1
                  }}
                  onClick={() => {
                    if (!isProfileLocked) {
                      navigate(`/documents/sign/${record.id}`);
                    }
                  }}
                >
                  <EditOutlined />
                </div>
              </Tooltip>
            )}
          </Flex>
        ) : null
      }
    },
    {
      title: 'Copy link',
      dataIndex: 'clipboard',
      key: 'clipboard',
      width:220,
      render: (_, record) => {
        // Use signed document URL if available
        const urlToCopy = record.is_signed && record.signed_document_file
          ? record.signed_document_file
          : record.document_file;
        return (
          <div className='d-flex justify-content-center align-items-center'>
            <CopyOutlined className='cursor-pointer' onClick={() => copyToClipboard(urlToCopy)} />
          </div>
        )
      }
    },
  ];

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      notify("success", "Link copied to clipboard");
    } catch (err) {
      notify("error", "Error occurred while copying link");
    }
  }

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };


  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const [selectionType] = useState('checkbox');

  return (
    <>
      {isProfileLocked && (
        <Alert
          message="Profile Locked"
          description="Document upload, download, and deletion functionality is disabled while your profile is locked."
          type="warning"
          showIcon
          style={{ margin: '10px' }}
        />
      )}
      <div style={{ padding: 10 }}>
        <Flex gap='large' justify='space-between'>
          <Flex gap='large' align='center' wrap>
            <div>
              Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalRows)} of {totalRows} documents
            </div>

            <FilterBar
              searchValue={searchTermLive}
              onSearchChange={setSearchTermLive}
              onSearch={onSearch}
              searchPlaceholder="Search documents"
              onRefresh={() => fetchAllDocuments()}
              refreshing={loading}
              addLabel="Add Documents"
              addIcon={<UserAddOutlined />}
              onAdd={() => navigate("/documents/new")}
            />
          </Flex>
        </Flex>

        {/* Bulk Actions Toolbar */}
        {selectedRowKeys.length > 0 && (
          <Flex gap="middle" style={{ marginTop: 10, padding: '8px 12px', backgroundColor: '#e6f7ff', borderRadius: '4px', alignItems: 'center' }}>
            <span style={{ fontWeight: 500 }}>{selectedRowKeys.length} document(s) selected</span>
            <Button size="small" onClick={handleClearSelection}>
              Clear Selection
            </Button>
            <Button size="small" onClick={handleBulkDownload} disabled={isProfileLocked}>
              <DownloadOutlined /> Download Selected
            </Button>
            <Button size="small" danger onClick={handleBulkDelete} disabled={isProfileLocked}>
              <DeleteOutlined /> Delete Selected
            </Button>
          </Flex>
        )}

        <div style={{ marginTop: 20 }}>
          <Table
            rowSelection={{
              type: selectionType,
              ...rowSelection,
            }}
            loading={loading}
            bordered
            size="small"
            dataSource={dataSource}
            columns={columns}
            rowKey={row => row.id}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalRows,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              pageSizeOptions: ['10', '25', '50', '100'],
              onChange: (page, size) => {
                setFilters({ page: size !== pageSize ? 1 : page, pageSize: size });
              },
            }}
            scroll={{
                x: "max-content", // Horizontal scroll
                y: "calc(100vh - 350px)", // Vertical scroll
              }}
              sticky
          />
        </div>

      </div>

      {/* Pdf Preview Modal Start */}
      <PdfPreviewModal
        pdfPreviewData={pdfPreviewData}
        previewPdfVisible={previewPdfVisible}
        setPreviewPdfVisible={setPreviewPdfVisible}
      />
      {/* Pdf Preview Modal End */}
    </>
  );
};
export default DocumentsTable;